import React from 'react';
import { Loader2 } from 'lucide-react';

export const formatContextPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const pct = n <= 1 ? n * 100 : n;
  return Math.min(100, Math.max(0, pct));
};

export const formatCompactTokens = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const abs = Math.abs(n);
  if (abs < 1000) return String(Math.round(n));
  if (abs < 1_000_000) {
    const k = n / 1000;
    const digits = abs >= 10_000 ? 0 : 1;
    return `${k.toFixed(digits).replace(/\.0$/, '')}k`;
  }
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
};

const meterTone = (percent) => {
  if (percent >= 85) return { fill: 'bg-rose-500', label: 'text-rose-600' };
  if (percent >= 65) return { fill: 'bg-amber-500', label: 'text-amber-600' };
  return { fill: 'bg-sky-500', label: 'text-sky-600' };
};

const ContextUsageBar = ({ usage, isLoading = false }) => {
  const percent = formatContextPercent(usage?.context_utilization_pct);
  const remaining = formatCompactTokens(usage?.remaining_context_tokens);
  const inputTokens = formatCompactTokens(usage?.input_tokens);
  const tone = meterTone(percent ?? 0);
  const hasData = percent != null || remaining != null;

  if (!isLoading && !hasData) return null;

  const tooltip = [
    percent != null ? `${percent.toFixed(1)}% used` : null,
    remaining ? `${remaining} remaining` : null,
    inputTokens ? `${inputTokens} in last turn` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="px-3 pt-2 pb-1.5" title={tooltip || undefined}>
      <div className="flex items-baseline justify-between gap-3 text-[11px] leading-none">
        <span className="font-medium text-gray-500">Context</span>
        {isLoading ? (
          <span className="inline-flex items-center gap-1 text-gray-400">
            <Loader2 size={11} className="animate-spin text-sky-500" />
            Updating
          </span>
        ) : (
          <span className="tabular-nums text-gray-500">
            <span className={`font-semibold ${tone.label}`}>
              {percent != null ? `${percent.toFixed(percent < 10 ? 1 : 0)}%` : '—'}
            </span>
            {remaining ? <span className="text-gray-400"> · {remaining} left</span> : null}
          </span>
        )}
      </div>
      <div
        className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100"
        role="progressbar"
        aria-label="Context window used"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent != null ? Math.round(percent) : undefined}
      >
        <div
          className={`h-full rounded-full transition-[width,background-color] duration-300 ease-out ${tone.fill}`}
          style={{ width: `${percent ?? 0}%` }}
        />
      </div>
    </div>
  );
};

export default ContextUsageBar;
